import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { Title } from "../components/common/Title";
import PaginationControls from "../components/boards/PaginationControls";
import { formatDate } from "../components/epicLeaderboard/format";
import type { GquestMobResponse } from "../types/gquestMobs";
import type { GquestRange } from "../types/gquests";
import { useComponentTracking, useTimeTracking } from "../utils/analytics";
import "../components/epicLeaderboard/epicLeaderboard.css";
import "./gquests.css";

const DEFAULT_PAGE_LIMIT = 10;
const PAGE_LIMIT_OPTIONS = [10, 20, 50, 100];
const positiveInteger = (value: string | null) =>
  value && /^\d+$/.test(value) && Number(value) > 0 ? Number(value) : undefined;
const pageLimit = (value: string | null) => {
  const parsed = positiveInteger(value);
  return parsed && PAGE_LIMIT_OPTIONS.includes(parsed) ? parsed : DEFAULT_PAGE_LIMIT;
};
const rangeLabel = (min: number, max: number) =>
  min === max ? String(min) : `${min}–${max}`;

const Tile = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
  <div className="epic-lb-card gquest-tile">
    <div className="epic-lb-card-title">{label}</div>
    <div className="epic-lb-tile-value">{value}</div>
    {sub && <div className="epic-lb-muted">{sub}</div>}
  </div>
);

const GquestMobs = () => {
  useComponentTracking("GquestMobs");
  useTimeTracking("GquestMobs");
  const [searchParams, setSearchParams] = useSearchParams();
  const rangeId = positiveInteger(searchParams.get("rangeId"));
  const search = searchParams.get("search") ?? "";
  const page = positiveInteger(searchParams.get("page")) ?? 1;
  const limit = pageLimit(searchParams.get("limit"));
  const [searchInput, setSearchInput] = useState(search);
  const lastWrittenSearch = useRef(search);
  const [ranges, setRanges] = useState<GquestRange[]>([]);
  const [data, setData] = useState<GquestMobResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);

  const updateParams = useCallback((changes: Record<string, string | undefined>, resetPage = true) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      Object.entries(changes).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      if (resetPage && !("page" in changes)) next.delete("page");
      return next;
    });
  }, [setSearchParams]);

  useEffect(() => {
    if (search !== lastWrittenSearch.current) {
      lastWrittenSearch.current = search;
      setSearchInput(search);
    }
  }, [search]);

  useEffect(() => {
    const nextSearch = searchInput.trim();
    if (nextSearch === lastWrittenSearch.current) return undefined;
    const timer = window.setTimeout(() => {
      lastWrittenSearch.current = nextSearch;
      updateParams({ search: nextSearch || undefined });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, updateParams]);

  useEffect(() => {
    const controller = new AbortController();
    axios.get<{ ranges: GquestRange[] }>(`${process.env.REACT_APP_API}public/gquest-mob-ranges`, { signal: controller.signal })
      .then(response => { if (!controller.signal.aborted) setRanges(response.data.ranges); })
      .catch(requestError => {
        if (!axios.isCancel(requestError) && !controller.signal.aborted) {
          console.error("Failed to fetch global quest ranges", requestError);
          setError(true);
        }
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!rangeId) {
      setData(null);
      setLoading(false);
      setError(false);
      return undefined;
    }
    const controller = new AbortController();
    const params: Record<string, string | number> = { page, limit };
    if (rangeId) params.rangeId = rangeId;
    if (search) params.search = search;
    setLoading(true);
    setError(false);
    axios.get<GquestMobResponse>(`${process.env.REACT_APP_API}public/gquest-mobs`, { params, signal: controller.signal })
      .then(response => { if (!controller.signal.aborted) setData(response.data); })
      .catch(requestError => {
        if (axios.isCancel(requestError) || controller.signal.aborted) return;
        console.error("Failed to fetch global quest mobs", requestError);
        setError(true);
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [rangeId, search, page, limit, reload]);

  return <Container className="py-4">
    <Title text="GQ Mob Guide" />
    <p className="text-light mb-4">Historical GQ targets by level range. Confirmed areas are shown when available.</p>
    <Form onSubmit={event => event.preventDefault()} className="mb-4 gquest-filters">
      <Row className="g-3 text-light">
        <Col xs={12} md={5}>
          <Form.Label>Level range</Form.Label>
          <Form.Select value={rangeId ?? ""} onChange={event => updateParams({ rangeId: event.target.value || undefined })}>
            <option value="" disabled>Select a level range</option>
            {ranges.map(range => <option key={range.id} value={range.id}>{rangeLabel(range.levelMin, range.levelMax)}</option>)}
          </Form.Select>
        </Col>
        <Col xs={12} md={7}>
          <Form.Label>Mob name</Form.Label>
          <Form.Control value={searchInput} onChange={event => setSearchInput(event.target.value)} placeholder="Search mob name" />
        </Col>
      </Row>
    </Form>
    {error && <div className="epic-lb-card text-center mb-4"><div className="mb-2">Couldn&apos;t load GQ mob data.</div><Button size="sm" variant="outline-light" onClick={() => setReload(value => value + 1)}>Retry</Button></div>}
    {!rangeId && !error && <div className="epic-lb-card text-center">Select a level range to see its historical GQ mob targets.</div>}
    {loading && !data ? <div className="text-center my-5"><Spinner animation="border" variant="light" /></div> : data && !error && <div className={loading ? "epic-lb-refetching" : undefined}>
      <Row className="g-3 mb-4">
        <Col xs={6} lg={3}><Tile label="GQs recorded" value={data.summary.totalGquests.toLocaleString()} /></Col>
        <Col xs={6} lg={3}><Tile label="Unique mobs" value={data.summary.uniqueMobs.toLocaleString()} /></Col>
        <Col xs={6} lg={3}><Tile label="Total mobs" value={data.summary.totalRequired.toLocaleString()} /></Col>
        <Col xs={6} lg={3}><Tile label="Avg. targets" value={data.summary.averageTargetsPerGquest} sub="per global quest" /></Col>
      </Row>
      <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
        <div className="epic-lb-card-title gquest-history-title">Likely mob targets</div>
        <Form.Select aria-label="Rows per page" className="gquest-page-size" value={limit} onChange={event => updateParams({ limit: event.target.value })}>
          {PAGE_LIMIT_OPTIONS.map(option => <option key={option} value={option}>{option} per page</option>)}
        </Form.Select>
      </div>
      {data.rows.length === 0 ? <div className="epic-lb-card text-center">No mobs match these filters.</div> : <div className="epic-lb-table-scroll"><table className="epic-lb-table gquest-mob-table"><colgroup><col className="gquest-mob-col-name" /><col className="gquest-mob-col-area" /><col className="gquest-mob-col-seen" /><col className="gquest-mob-col-frequency" /><col className="gquest-mob-col-average" /><col className="gquest-mob-col-last" /></colgroup><thead><tr><th>Mob</th><th>Area</th><th>Seen in GQs</th><th>Frequency</th><th>Avg. per GQ</th><th>Last seen</th></tr></thead><tbody>{data.rows.map(row => <tr key={row.name}><td className="gquest-mob-name">{row.name}</td><td className="gquest-area">{row.area ?? "—"}</td><td>{row.seenIn}</td><td>{row.frequency}%</td><td>{row.averageRequired}</td><td>{formatDate(row.lastSeen)}</td></tr>)}</tbody></table></div>}
      {data.pagination.totalPages > 1 && <PaginationControls pagination={data.pagination} onPageChange={nextPage => updateParams({ page: String(nextPage) }, false)} />}
    </div>}
  </Container>;
};

export default GquestMobs;
